import type { ColumnDef, FilterFn, Row } from "@tanstack/react-table";
import type { AutoCombineEntryV2 } from "@/shared/cache";
import { columns as baseColumns, formatDate } from "./columns";
import { enumFilterFn } from "./filters/enumFilterFn";
import { formatTimekeeperName, type Timekeeper } from "./timekeepers";
import { makeColumnOrderParser, makeVisibilityParser } from "./viewParams";

// Entry-group mode reuses the shared column definitions verbatim — an
// entry-group row carries display fields plus nested source entries, so the
// cells render identically.

export const AUTO_COLUMN_LABELS: Record<string, string> = {
	date: "Date",
	timekeeper: "Timekeeper",
	matter: "Matter",
	codes: "Codes",
	description: "Narrative",
	status: "Status",
	billableCategory: "Billing",
	units: "Units",
	application: "App",
};

export const AUTO_DEFAULT_COLUMN_ORDER = Object.keys(AUTO_COLUMN_LABELS);

export const AUTO_DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
	// App is available but off by default — the entry group's app is a projection;
	// the real per-source app lives on the child entries instead.
	application: false,
	// Hidden filter-only columns (shared with normal mode).
	client: false,
	task: false,
	activity: false,
	matterName: false,
	billable: false,
	unitsSeconds: false,
};

/**
 * Entry groups show only the date — the per-source times live on the
 * child rows below, where they read as a timeline, so a group-level time would be
 * redundant and ambiguous (start? end? creation?).
 */
function AutoCombineDateCell({ row }: { row: Row<AutoCombineEntryV2> }) {
	return (
		<div className="whitespace-nowrap tabular-nums text-foreground">
			{formatDate(row.original.date)}
		</div>
	);
}

function autoCombineDateSortKey(entry: AutoCombineEntryV2): number {
	if (entry.lastEntryAt) {
		const timestamp = Date.parse(entry.lastEntryAt);
		if (!Number.isNaN(timestamp)) return timestamp;
	}
	const fallback = Date.parse(`${entry.date}T00:00:00Z`);
	return Number.isNaN(fallback) ? 0 : fallback;
}

function autoTimekeeperColumn(
	timekeepers: readonly Timekeeper[],
): ColumnDef<AutoCombineEntryV2> {
	return {
		id: "timekeeper",
		accessorFn: (entry) => entry.timekeeperId ?? "",
		enableGrouping: false,
		enableSorting: false,
		filterFn: enumFilterFn as FilterFn<AutoCombineEntryV2>,
		size: 132,
		minSize: 112,
		header: () => (
			<span className="-ml-1 text-sm font-medium text-muted-foreground">
				Timekeeper
			</span>
		),
		cell: ({ getValue }) => {
			const id = getValue<string | null>();
			const member = timekeepers.find((timekeeper) => timekeeper.id === id);
			return (
				<span className="whitespace-nowrap text-foreground">
					{member ? formatTimekeeperName(member) : (id ?? "–")}
				</span>
			);
		},
	};
}

// Safe widening: every base cell reads only display fields carried by
// `AutoCombineEntryV2`. The date cell is swapped for a span-aware variant
// (see above); everything else renders verbatim.
export function getAutoColumns(
	timekeepers: readonly Timekeeper[] | null,
): ColumnDef<AutoCombineEntryV2>[] {
	const mapped = (
		baseColumns as unknown as ColumnDef<AutoCombineEntryV2>[]
	).map((col) =>
		col.id === "date"
			? {
					...col,
					cell: AutoCombineDateCell,
					sortingFn: (
						a: Row<AutoCombineEntryV2>,
						b: Row<AutoCombineEntryV2>,
					) => {
						const timeOrder =
							autoCombineDateSortKey(a.original) -
							autoCombineDateSortKey(b.original);
						return timeOrder || a.original.id.localeCompare(b.original.id);
					},
				}
			: col.id === "_select"
				? { ...col, size: 40 }
				: col,
	);

	if (!timekeepers) return mapped;

	const [selectColumn, ...rest] = mapped;
	return [selectColumn, autoTimekeeperColumn(timekeepers), ...rest];
}

export const autoColumns = getAutoColumns(null);

export const autoVisibilityParser = makeVisibilityParser(
	AUTO_DEFAULT_COLUMN_VISIBILITY,
);
export const autoColumnOrderParser = makeColumnOrderParser(
	AUTO_DEFAULT_COLUMN_ORDER,
);
